import { useSelector } from 'react-redux';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { keepPreviousData } from '@tanstack/react-query';
import { LearningPath, useRepositoryQuery } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText, useLocalizeEnum } from '@mews/framework';
import { IconName } from '@optimus-web/icons';
import { Icon, Typography } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { values } from '@mews/fp';
import { useCurrentEnterpriseId, useCurrentProfileId } from '@mews-commander/core';
import { hasPlatformScopeWithAccessAllEnterprises } from '@mews-commander/legacy-entities/src/ducks/scopes';
import {
    isCurrentProfileAdmin,
    getCurrentProfileScopeEnterpriseId,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { RolesAndPermissionsOption } from '../types';
import { FormOption, orderByLabel } from '../utils';
import { canManageRoles } from '../../roles/privileges';
import { useUserType } from './useUserType';

export const useLearningPathsOptions = () => {
    const localizeEnum = useLocalizeEnum();

    return pipe(
        LearningPath,
        values,
        array.map((enumValue) => ({
            key: enumValue,
            value: enumValue,
            label: localizeEnum('LearningPath', enumValue),
        })),
        array.sortBy([orderByLabel])
    );
};

const getRolesAndPermissionsOptionsValues = ({
    isRoleManagementEnabled,
    isUserAdmin,
    isPortfolioCentralUser,
}: {
    isRoleManagementEnabled: boolean;
    isUserAdmin: boolean;
    isPortfolioCentralUser: boolean;
}) => [
    ...(isRoleManagementEnabled && !isPortfolioCentralUser
        ? [
              {
                  icon: IconName.Employees,
                  label: TextKey.UserProfilePermissionTypeRolesTitle,
                  value: RolesAndPermissionsOption.Roles,
                  description: TextKey.UserProfilePermissionTypeRolesDescription,
              },
          ]
        : []),
    ...(!isPortfolioCentralUser
        ? [
              {
                  icon: IconName.Settings,
                  label: TextKey.UserProfilePermissionTypeCustomizedPermissionsTitle,
                  value: RolesAndPermissionsOption.CustomizedPermissions,
                  description: TextKey.UserProfilePermissionTypeCustomizedPermissionsDescription,
              },
              {
                  icon: IconName.Lock,
                  label: TextKey.UserProfilePermissionTypeNoPermissionsTitle,
                  value: RolesAndPermissionsOption.NoPermissions,
                  description: TextKey.UserProfilePermissionTypeNoPermissionsDescription,
              },
          ]
        : []),
    {
        icon: IconName.Security,
        label: TextKey.UserProfilePermissionTypeAdminTitle,
        value: RolesAndPermissionsOption.Admin,
        description: TextKey.UserProfilePermissionTypeAdminDescription,
        disabled: !isUserAdmin,
    },
];

export const useRolesAndPermissionsOptions = () => {
    const isRoleManagementEnabled = useSelector(canManageRoles);
    const isUserAdmin: boolean = useSelector(isCurrentProfileAdmin);
    const canAccessAllEnterprises = useSelector(hasPlatformScopeWithAccessAllEnterprises);
    const { isPortfolioCentralUser } = useUserType();

    return pipe(
        {
            isRoleManagementEnabled,
            isUserAdmin,
            isPortfolioCentralUser,
        },
        getRolesAndPermissionsOptionsValues,
        array.map(({ icon, label, value, description, disabled }) => ({
            media: <Icon value={icon} size="medium" />,
            label: (
                <Typography textStyle="bodySmallStrong" as="div">
                    <LocalizedText textKey={label} />
                </Typography>
            ),
            value,
            name: 'rolesAndPermissions',
            description: (
                <Typography textStyle="bodySmallStrong" variant="secondary" as="div">
                    <LocalizedText textKey={description} />
                </Typography>
            ),
            ...(!canAccessAllEnterprises && disabled ? { disabled: true } : {}),
            dataTestId: value,
        }))
    );
};

export const useDepartmentsOptions = (): FormOption[] => {
    const enterpriseId = useCurrentEnterpriseId();
    const { data } = useRepositoryQuery(
        'departments',
        'getAll',
        { enterpriseId },
        {
            placeholderData: keepPreviousData,
        }
    );

    const departments = pipe(
        data?.departments || [],
        array.map((department) => ({
            key: department.id,
            value: department.id,
            label: department.name,
        })),
        array.sortBy([orderByLabel])
    );

    return [
        {
            key: '0',
            value: '',
            label: UnicodeEntity.Dash,
        },
        ...departments,
    ];
};

export const useCashiersOptions = (): FormOption[] => {
    const enterpriseId = useCurrentEnterpriseId();
    const { data } = useRepositoryQuery(
        'cashiers',
        'getAll',
        { enterpriseId },
        {
            placeholderData: keepPreviousData,
        }
    );

    const cashiers = data?.cashiers || [];

    return pipe(
        cashiers,
        array.map((cashier) => ({
            key: cashier.id,
            value: cashier.id,
            label: cashier.name,
        })),
        array.sortBy([orderByLabel])
    );
};

export const useDefaultSuperiorProfileId = () => {
    const scopeEnterpriseId = useSelector(getCurrentProfileScopeEnterpriseId);
    const enterpriseId = useCurrentEnterpriseId();
    const profileId = useCurrentProfileId();

    return enterpriseId === scopeEnterpriseId ? profileId : undefined;
};
